import { useState } from "react";
import { useGame } from "@/game/GameContext";
import { BUSINESS_TYPES } from "@/game/gameData";
import PixelButton from "@/components/PixelButton";
import { sfx } from "@/game/audioEngine";
import { toast } from "sonner";
import { Shuffle } from "lucide-react";

// Random fill pools — kept playful and lightly multicultural to match the
// character roster. Used by the "Random" button when a player wants the AI
// to make stuff up for them.
const FIRST_NAMES = [
  "Alex", "Priya", "Marcus", "Yuki", "Jordan", "Maya", "Diego", "Aaliyah",
  "Kenji", "Imani", "Casey", "Leila", "Theo", "Nia", "Tomás", "Mei",
  "Riley", "Zara", "Mateo", "Naomi", "Sam", "Rohan", "Ada", "Sofia",
  "Hassan", "Vivian", "Felix", "Anya", "Devon", "Lucia",
];

const BUSINESS_NAMES = [
  "Frontier Coffee Co.",
  "Pixel Cactus Studio",
  "The Quirky Quill",
  "Wagon Wheel Web Co.",
  "Wandering Hat Co.",
  "Tumbleweed Tax Prep",
  "Saguaro Soap Society",
  "Cactus & Code",
  "Boots & Bytes",
  "Trailblazer Tutoring",
  "Hilltop Hounds Pet Care",
  "Mesquite & Maple",
  "The Daily Doodler",
  "Sunup Surveyors",
  "Last Call Catering",
  "Pioneer Pizza Pies",
  "Compass & Quill",
  "Roundhouse Realtors",
  "Riverbend Roastery",
  "Goldpan Graphics",
  "Lone Pine Landscaping",
  "Penny Press Printing",
  "Stitchwork Sisters",
  "Outpost Outfitters",
  "High Plains Hobby",
];

const MISSIONS = [
  "Helping small businesses find their voice",
  "Hand-stitched goods for everyday adventurers",
  "Coffee that tastes like sunrise",
  "Tutoring kids in math and music",
  "Pet portraits in pixel art",
  "Wedding photography with character",
  "Plant-based comfort food",
  "Boutique web design for craftspeople",
  "Custom maps for trail runners",
  "Mobile dog grooming on the trail",
  "Vintage furniture restoration",
  "Local honey and beeswax candles",
  "Outdoor gear repair and resale",
  "Online courses for new founders",
  "Bookkeeping for creatives",
  "Home-cooked meal delivery",
  "Bicycle tours through historic towns",
  "Pottery wheel lessons",
  "Handmade leather goods",
  "Drone photography for real estate",
];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

export default function BusinessSetupScreen() {
  const { state, dispatch } = useGame();
  const [form, setForm] = useState({
    playerName: state.player.playerName || "",
    businessName: state.player.businessName || "",
    businessType: state.player.businessType || "",
    productMission: state.player.productMission || "",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const randomize = () => {
    sfx.spin();
    setForm({
      playerName: pick(FIRST_NAMES),
      businessName: pick(BUSINESS_NAMES),
      businessType: pick(BUSINESS_TYPES),
      productMission: pick(MISSIONS),
    });
    toast.success("Random venture rolled. Tweak anything you like.");
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.playerName.trim() || !form.businessName.trim() || !form.businessType) {
      toast.error("Please share your first name, business name and type.");
      return;
    }
    dispatch({
      type: "SET_PLAYER",
      fields: {
        ...form,
        audience: "your audience",
        goal: "More leads",
      },
    });
    dispatch({ type: "GOTO", screen: "wheel" });
  };

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <h2 className="pixel-font text-xl md:text-2xl text-[#F58634] text-center mb-2">
          TELL US ABOUT YOUR "VENTURE"
        </h2>
        <p className="vt323 text-xl text-white/80 text-center mb-6 max-w-md mx-auto">
          A quick setup so the trail can be personalized for you.
          <br />
          <span className="text-white/95">Have fun, make up a fake name and business if you'd like!</span>
        </p>

        <form onSubmit={submit} className="pixel-panel space-y-4" data-testid="business-form">
          <div className="flex justify-end">
            <button
              type="button"
              data-testid="randomize-button"
              onClick={randomize}
              className="pixel-font text-[10px] bg-[#0F172A] text-white border-4 border-black px-3 py-2 shadow-[3px_3px_0_0_#F58634] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#F58634] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all flex items-center gap-2"
            >
              <Shuffle className="w-[1em] h-[1em]" strokeWidth={3} aria-hidden="true" />
              <span>Random</span>
            </button>
          </div>

          <div>
            <label className="pixel-font text-[10px] mb-1 block">FIRST NAME *</label>
            <input
              data-testid="player-name-input"
              className="pixel-input"
              placeholder="e.g. Jamie"
              value={form.playerName}
              onChange={(e) => setField("playerName", e.target.value)}
            />
          </div>
          <div>
            <label className="pixel-font text-[10px] mb-1 block">BUSINESS NAME *</label>
            <input
              data-testid="business-name-input"
              className="pixel-input"
              placeholder="e.g. Frontier Coffee Co."
              value={form.businessName}
              onChange={(e) => setField("businessName", e.target.value)}
            />
          </div>
          <div>
            <label className="pixel-font text-[10px] mb-1 block">BUSINESS TYPE *</label>
            <select
              data-testid="business-type-select"
              className="pixel-select"
              value={form.businessType}
              onChange={(e) => setField("businessType", e.target.value)}
            >
              <option value="">Select a type…</option>
              {BUSINESS_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="pixel-font text-[10px] mb-1 block">PRODUCT, SERVICE OR MISSION</label>
            <input
              data-testid="business-mission-input"
              className="pixel-input"
              placeholder="What do you do or sell?"
              value={form.productMission}
              onChange={(e) => setField("productMission", e.target.value)}
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-3">
            <PixelButton
              type="button"
              variant="secondary"
              onClick={() => dispatch({ type: "GOTO", screen: "character" })}
            >
              ← Back
            </PixelButton>
            <PixelButton type="submit" data-testid="business-continue">
              Next: Startup Funds ▶
            </PixelButton>
          </div>
        </form>
      </div>
    </div>
  );
}
